class TradeInForm extends HTMLElement {
  static observedAttributes = ['data-view'];

  constructor() {
    super();
    this.stepNumber = this.querySelector('#trade-in-step-number');
    this.progressBarFill = this.querySelector('.trade-in-progress-bar-fill');
    this.form = /** @type {HTMLFormElement} */ (this.querySelector('form#trade-in-form'));
    this.contactForm = /** @type {HTMLFormElement} */ (this.querySelector('form#trade-in-contact-form'));
    this.confirmSubmitButton = this.querySelector('.trade-in-confirm-submit-button');
    this.productBrandSelect = this.querySelector('#product-brand');
    this.uploadArea = this.querySelector('.trade-in-upload-area');
    this.fileInput = this.uploadArea && this.uploadArea.querySelector('#images');
    this.productModelSelect = this.querySelector('#product-model');
    this.getEstimateButton = this.querySelector('.trade-in-submit-button');
    this.continueButton = this.querySelector('.trade-in-continue-button');
    this.taskId = this.getAttribute('data-task-id');
    this.estimateItems = this.querySelector('.trade-in-estimate-items');
    this.estimateCount = this.querySelector('#trade-in-estimate-count');
    this.estimateTotalValue = this.querySelector('#trade-in-estimate-total');
    this.addAnotherItemButton = this.querySelector('.trade-in-add-another-item-button');
    this.backButton = this.querySelector('.trade-in-back-button');
    this.formErrorMessage = this.querySelector('.trade-in-form-error-message');
    this.storefrontToken = this.getAttribute('data-storefront-token') ?? '14afa530144739b341cc5015bc608339';
    /** @type {Record<string, any[]>} */
    this.brandProductsCache = {};
  }

  connectedCallback() {
    this.initBrands();
  }

  async initBrands() {
    try {
      const query = `{
        metaobjects(type: "trade_in_products", first: 250) {
          edges {
            node {
              handle
              field(key: "brand") {
                value
              }
            }
          }
        }
      }`;

      const response = await fetch('/api/2024-10/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({ query }),
      });

      const data = await response.json();
      const metaobjects = data?.data?.metaobjects?.edges;

      if (metaobjects && metaobjects.length > 0) {
        this.brands = metaobjects
          .map((/** @type {any} */ edge) => ({ handle: edge.node.handle, brand: edge.node.field.value }))
          .sort((/** @type {any} */ a, /** @type {any} */ b) => a.brand.localeCompare(b.brand));

        if (this.productBrandSelect) {
          this.productBrandSelect.innerHTML += this.brands
            .map((/** @type {any} */ b) => `<option value="${b.handle}">${b.brand}</option>`)
            .join('');
        }

        this.formListeners();
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.form.classList.remove('loading');
    }
  }

  async fetchBrandProducts(/** @type {string} */ brandHandle) {
    if (this.brandProductsCache[brandHandle]) {
      return this.brandProductsCache[brandHandle];
    }

    const query = `query getMetaobject($handle: MetaobjectHandleInput!) {
      metaobject(handle: $handle) {
        field(key: "products") {
          value
        }
      }
    }`;

    const response = await fetch('/api/2024-10/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontToken,
      },
      body: JSON.stringify({
        query,
        variables: { handle: { type: 'trade_in_products', handle: brandHandle } },
      }),
    });

    const data = await response.json();
    const products = JSON.parse(data?.data?.metaobject?.field?.value || '[]');
    this.brandProductsCache[brandHandle] = products;
    return products;
  }

  attributeChangedCallback(/** @type {string} */ name, /** @type {string} */ oldValue, /** @type {string} */ newValue) {
    console.log(name, oldValue, newValue);
    if (name === 'data-view') {
      if (newValue === "form+equipment" || newValue === "estimate") {
        (/** @type {HTMLButtonElement} */ (this.continueButton)).disabled = false;
      } else {
        (/** @type {HTMLButtonElement} */ (this.continueButton)).disabled = true;
      }

      if (newValue === "form+equipment" || newValue === "estimate" || newValue === "details") {
        (/** @type {HTMLButtonElement} */ (this.backButton)).disabled = false;
      } else {
        (/** @type {HTMLButtonElement} */ (this.backButton)).disabled = true;
      }

      if (newValue === "details") {
        this.stepNumber && (this.stepNumber.textContent = "2");
        /** @type {HTMLDivElement} */ (this.progressBarFill).style.width = "100%";
        /** @type {HTMLButtonElement} */ (this.confirmSubmitButton).disabled = false;
      } else {
        this.stepNumber && (this.stepNumber.textContent = "1");
        /** @type {HTMLDivElement} */ (this.progressBarFill).style.width = "50%";
        /** @type {HTMLButtonElement} */ (this.confirmSubmitButton).disabled = true;
      }
    }
  }

  /**
   * Handles the form listeners.
  */
  formListeners() {
    if (this.form) {
      this.handleProductBrandSelect();
      this.fileUpload();
      this.handleSubmit();
      this.handleAddAnotherItem();
      this.handleBackButton();
      this.handleContinueButton();
    }

    if (this.contactForm) {
      this.handleContactFormSubmit();
    }
  }

  /**
   * Handles the contact form submit event.
  */
  handleContactFormSubmit() {
    if (this.contactForm) {
      this.contactForm.addEventListener('submit', async (/** @type {SubmitEvent} */ e) => {
        e.preventDefault();
        const formData = new FormData(this.contactForm);
        const redirectUrl = this.contactForm.getAttribute("data-redirect-url");

        const items = this.querySelectorAll('trade-in-estimate-item');
        const itemsData = [...items].map((/** @type {TradeInEstimateItem} */ item) => item.formDataObject);
        console.log(itemsData);

        const contactFormData = {
          action: 'submit_contact_form',
          name: formData.get('name')?.toString(),
          email: formData.get('email')?.toString(),
          phone: formData.get('phone')?.toString(),
          business_type: formData.get('business_type')?.toString() === 'yes' ? true : false,
          trade_method: formData.get('trade_method')?.toString(),
          store_location: formData.get('store_location')?.toString(),
          items: itemsData,
        };

        const response = await fetch(`https://www.teds.com.au/apps/datajet/task/${this.taskId}`, { 
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(contactFormData),
        });
        const data = await response.json();
        
        if (data.status === "success") {
          this.contactForm.reset();
          if (redirectUrl && redirectUrl !== '') {
            window.location.href = redirectUrl;
          }
        }

      });
    }
  }

  /**
   * Handles the back button click event.
  */
  handleBackButton() {
    if (this.backButton) {
      this.backButton.addEventListener('click', () => {
        this.setAttribute('data-view', 'estimate');
      });
    }
  }

  /**
   * Handles the continue button click event.
  */
  handleContinueButton() {
    if (this.continueButton) {
      this.continueButton.addEventListener('click', () => {
        this.setAttribute('data-view', 'details');
      });
    }
  }

  handleProductBrandSelect() {
    if (!this.productBrandSelect || !this.productModelSelect) return;

    this.productBrandSelect.addEventListener('change', async (/** @type {any} */ e) => {
      const brandHandle = e.target.value;
      const variantValue = e.detail?.variantValue;

      const modelSelect = /** @type {HTMLSelectElement} */ (this.productModelSelect);
      modelSelect.innerHTML = `<option value="">Select Model</option>`;

      if (!brandHandle) return;

      const products = await this.fetchBrandProducts(brandHandle);

      if (!products.length) return;

      modelSelect.innerHTML = `
        <option value="">Select Model</option>
        ${products.map((/** @type {any} */ item) => `
          <option value="${item.variant_id}">${item.product_title}</option>
        `).join('')}
      `;

      modelSelect.disabled = false;

      if (variantValue) {
        modelSelect.value = variantValue;
      }
    });
  }



  /**
   * Handles the form submit event.
  */
  handleSubmit() {
    if (this.form) {
      this.form.addEventListener('submit', async (/** @type {SubmitEvent} */ e) => {
        e.preventDefault();
        const formData = new FormData(this.form);
        let itemId = formData.get('item_id')?.toString();
        let action = 'update';
        if (!itemId || itemId === '') {
          itemId = crypto.randomUUID();
          action = 'add';
        }

        const modelSelect = /** @type {HTMLSelectElement} */ (this.productModelSelect);
        const selectedOption = modelSelect.options[modelSelect.selectedIndex];

        const brandSelect = /** @type {HTMLSelectElement} */ (this.productBrandSelect);
        const selectedBrandOption = brandSelect.options[brandSelect.selectedIndex];

        const formDataObject = {
          item_id: itemId,

          // BRAND
          brand: selectedBrandOption?.textContent?.trim(),
          brand_handle: brandSelect.value,

          // MODEL
          product_variant_id: formData.get('product_variant_id')?.toString(),
          product_variant_title: selectedOption?.textContent?.trim(),

          condition: formData.get('condition'),
          accessories: formData.getAll('accessories[]'),
          additional_details: formData.get('additional_details'),
          images: formData.getAll('images[]')
        };


        const multipliers = /** @type {Record<string, number>} */ ({ 'good-as-new': 1, 'excellent': 0.9, 'good': 0.8 });
        const condition = formData.get('condition')?.toString() ?? '';
        const multiplier = multipliers[condition];

        const brandProducts = this.brandProductsCache[brandSelect.value] || [];
        const selectedProduct = brandProducts.find((/** @type {any} */ p) => p.variant_id === formDataObject.product_variant_id);
        const tradeInValue = selectedProduct?.trade_in_value ?? null;

        const showError = (/** @type {string} */ msg) => {
          this.formErrorMessage && (this.formErrorMessage.innerHTML = `<span>${msg}</span>`);
          this.formErrorMessage && (this.formErrorMessage.classList.add('visible'));
          setTimeout(() => this.formErrorMessage && this.formErrorMessage.classList.remove('visible'), 5000);
        };

        const requiredAccessories = ['battery', 'charger'];
        const missingRequiredAccessories = requiredAccessories.filter(
          (requiredAccessory) => !formDataObject.accessories.includes(requiredAccessory)
        );

        if (missingRequiredAccessories.length) {
          showError('Battery and charger are required to get an estimate.');
          return;
        }

        if (!multiplier) {
          showError('Invalid condition selected.');
          return;
        }

        if (tradeInValue === null || tradeInValue === undefined) {
          showError('This product does not have a trade-in value.');
          return;
        }

        const trade_amount_value = parseFloat((tradeInValue * multiplier).toFixed(2));

        this.renderEstimate(formDataObject, trade_amount_value, action);
        this.form.reset();
        /** @type {HTMLInputElement} */ (this.form.querySelector('input[name="item_id"]')).value = '';
        this.productBrandSelect?.dispatchEvent(new Event('change'));
      });
    }
  }

  handleAddAnotherItem() {
    if (this.addAnotherItemButton) {
      this.addAnotherItemButton.addEventListener('click', () => {
        this.setAttribute('data-view', 'form+equipment');
      });
    }
  }

  /**
   * Renders the estimate screen.
   * @param {Object} formDataObject - The form data object.
   * @param {number} trade_amount_value - The trade amount value.
   * @param {string} action - The action to perform.
  */
  async renderEstimate(formDataObject, trade_amount_value, action) {
    const /** @type {any} */ data = formDataObject;
    if (action === 'update') {
      const item = this.querySelector(`trade-in-estimate-item[data-item-id="${data.item_id}"]`);
      if (item) {
        /** @type {TradeInEstimateItem} */ (item).formDataObject = data;
        /** @type {TradeInEstimateItem} */ (item).trade_amount_value = trade_amount_value;
        /** @type {TradeInEstimateItem} */ (item).render();
      }
    } else {
      data && await this.fillItems(data, trade_amount_value);
    }
    this.updateEstimateTotal();
    this.setAttribute('data-view', 'estimate');
  }

  /**
   * Fills the items in the estimate screen.
   * @param {Object} formDataObject - The form data object.
   * @param {number} trade_amount_value - The trade amount value.
  */
  async fillItems(formDataObject, trade_amount_value) {
    const item = new TradeInEstimateItem(formDataObject, trade_amount_value, this.form, this);
    this.estimateItems && (this.estimateItems.appendChild(item));
  }

  /**
   * Updates the estimate total.
  */
  updateEstimateTotal() {
    const itemsNodes = this.querySelectorAll('trade-in-estimate-item');
    if (itemsNodes.length === 0) {
      this.setAttribute('data-view', 'form');
      return;
    }
    this.estimateCount && (this.estimateCount.textContent = itemsNodes.length.toString());
    const itemsValues = [...itemsNodes].map((/** @type {TradeInEstimateItem} */ item) => item?.trade_amount_value);
    const totalTradeAmountValue = itemsValues.reduce((acc, curr) => acc + curr, 0);
    if (this.estimateTotalValue) {
      this.estimateTotalValue.textContent = `$${(totalTradeAmountValue || 0).toFixed(2)}`;
    }
  }



  /**
   * Fetches the product variants from the Shopify API.
   * @param {string} productHandle - The handle of the product.
  */
  async fetchProductVariants(productHandle) {
    const response = await fetch(`/products/${productHandle}.json`);
    const data = await response.json();
    const variants = data?.product?.variants?.map((/** @type {any} */ variant) => ({
      id: variant.id,
      title: variant.title,
    }));
    return variants;
  }

  fileUpload() {
    if (this.uploadArea && this.fileInput) {
      const fileInput = /** @type {HTMLInputElement} */ (this.fileInput);
      
      // Click to upload
      this.uploadArea.addEventListener('click', () => {
        fileInput.click();
      });
      
      // Drag and drop
      this.uploadArea.addEventListener('dragover', (e) => {
        e.preventDefault();
        const placeholder = this.uploadArea?.querySelector('.trade-in-upload-placeholder');
        if (placeholder) {
          /** @type {HTMLDivElement} */ (placeholder).style.borderColor = '#FCD900';
        }
      });
      
      this.uploadArea.addEventListener('dragleave', (e) => {
        e.preventDefault();
        const placeholder = this.uploadArea?.querySelector('.trade-in-upload-placeholder');
        if (placeholder) {
          /** @type {HTMLDivElement} */ (placeholder).style.borderColor = '#E0E0E0';
        }
      });
      
      this.uploadArea.addEventListener('drop', (e) => {
        e.preventDefault();
        const dragEvent = /** @type {DragEvent} */ (e);
        const placeholder = this.uploadArea?.querySelector('.trade-in-upload-placeholder');
        if (placeholder) {
          /** @type {HTMLDivElement} */ (placeholder).style.borderColor = '#E0E0E0';
        }
        
        if (dragEvent.dataTransfer?.files?.length) {
          fileInput.files = dragEvent.dataTransfer.files;
          this.updateFileDisplay();
        }
      });
      
      // Update display when files are selected
      fileInput.addEventListener('change', this.updateFileDisplay.bind(this));

      this.updateFileDisplay();
    }
  }

  updateFileDisplay() {
    const placeholder = this.uploadArea?.querySelector('.trade-in-upload-placeholder');
    const fileInput = /** @type {HTMLInputElement} */ (this.fileInput);
    if (fileInput?.files?.length && placeholder) {
      const fileCount = fileInput.files.length;
      placeholder.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="17 8 12 3 7 8"></polyline>
          <line x1="12" y1="3" x2="12" y2="15"></line>
        </svg>
        <p>${fileCount} file${fileCount > 1 ? 's' : ''} selected</p>
        <p class="trade-in-upload-hint">Click to change</p>
      `;
    }
  }
}


class TradeInEstimateItem extends HTMLElement {
  constructor( /** @type {Object} */ formDataObject, /** @type {number} */ trade_amount_value, /** @type {HTMLFormElement} */ form, /** @type {TradeInForm} */ tradeInForm) {
    super();
    this.formDataObject = formDataObject;
    this.form = form;
    this.trade_amount_value = trade_amount_value;
    this.tradeInForm = tradeInForm;
  }

  connectedCallback() {
    this.render();
  }

  disconnectedCallback() {
    console.log("Item removed")
    this.tradeInForm.updateEstimateTotal();
  }
  
  listeners() {
    this.removeElement();
    this.handleEdit();
  }

  removeElement() {
    if (this.removeButton) {
      this.removeButton.addEventListener('click', () => {
        console.log('removing item');
        this.remove();
      });
    }
  }

  handleEdit() {
    if (this.editButton) {
      const /** @type {any} */ data = this.formDataObject;
      this.editButton.addEventListener('click', async () => {
        const brandSelect = /** @type {HTMLSelectElement} */ (this.tradeInForm.productBrandSelect);
        brandSelect.value = data.brand_handle;
        brandSelect.dispatchEvent(
          new CustomEvent('change', { detail: { variantValue: data.product_variant_id } })
        );
        /** @type {HTMLSelectElement} */ (this.form.querySelector('#condition')).value = data.condition;
        for (const accessory of data.accessories) {
          /** @type {HTMLInputElement} */ (this.form.querySelector(`input[name="accessories[]"][value="${accessory}"]`)).checked = true;
        }
        /** @type {HTMLTextAreaElement} */ (this.form.querySelector('#additional-details')).value = data.additional_details;
        /** @type {HTMLInputElement} */ (this.form.querySelector('input[name="item_id"]')).value = data.item_id;
        this.tradeInForm.setAttribute('data-view', 'form+equipment');
      });
    }
  }

  render() {
    const data = /** @type {any} */ (this.formDataObject);
    this.setAttribute('data-item-id', data.item_id);
    const itemHTML = `
      <div class="trade-in-estimate-item">
        <div class="trade-in-estimate-item-info">
          <p>${data.brand} – ${data.product_variant_title}</p>
          <div>
            <span>${data.condition}</span>
            •
            <span> ${data.accessories.length} Accessories</span>
          </div>
        </div>
        <div class="trade-in-estimate-item-actions">
          <button class="trade-in-estimate-item-action-button" data-action="edit">Edit</button>
          <button class="trade-in-estimate-item-action-button" data-action="remove">Remove</button>
        </div>
      </div>
    `;
    
    this.innerHTML = itemHTML;

    this.editButton = this.querySelector('.trade-in-estimate-item-action-button[data-action="edit"]');
    this.removeButton = this.querySelector('.trade-in-estimate-item-action-button[data-action="remove"]');

    this.listeners();
  }

}

customElements.define('trade-in-estimate-item', TradeInEstimateItem);
customElements.define('trade-in-form', TradeInForm);         